import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Inbox, Loader2, ShieldAlert, Landmark } from "lucide-react";
import { assetUrl } from "../lib/api";

const TONES = {
  pending: "bg-amber-50 text-amber-800 border-amber-300",
  progress: "bg-sky-50 text-sky-800 border-sky-300",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-300",
  rejected: "bg-red-50 text-red-800 border-red-300",
  escalated: "bg-orange-50 text-orange-800 border-orange-300",
  neutral: "bg-slate-100 text-slate-700 border-slate-300",
};

export const StatusBadge = ({ status, statuses, testid }) => {
  const s = statuses?.find((x) => x.key === status) || { label: status?.replace(/_/g, " "), tone: "neutral" };
  return (
    <span data-testid={testid || "status-badge"} className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${TONES[s.tone] || TONES.neutral}`}>
      {s.label}
    </span>
  );
};

export const Pill = ({ tone = "neutral", children, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${TONES[tone]}`}>{children}</span>
);

export const PageHeader = ({ crumbs = [], title, subtitle, actions }) => (
  <div className="mb-6 rise">
    <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="flex items-center gap-1 text-xs text-slate-500 mb-2">
      <Link to="/" className="hover:text-slate-900">Home</Link>
      {crumbs.map((c) => (
        <span key={c} className="flex items-center gap-1"><ChevronRight className="h-3 w-3" />{c}</span>
      ))}
    </nav>
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 data-testid="page-title" className="text-2xl sm:text-3xl font-bold text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-600 max-w-3xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  </div>
);

export const EmptyState = ({ title = "No records yet", text, testid = "empty-state" }) => (
  <div data-testid={testid} className="flex flex-col items-center justify-center py-14 text-center">
    <Inbox className="h-9 w-9 text-slate-300" />
    <p className="mt-3 font-medium text-slate-700">{title}</p>
    {text && <p className="text-sm text-slate-500 mt-1 max-w-sm">{text}</p>}
  </div>
);

export const Loading = ({ label = "Loading…" }) => (
  <div data-testid="loading-state" className="flex items-center gap-2 py-10 justify-center text-slate-500 text-sm">
    <Loader2 className="h-4 w-4 animate-spin" /> {label}
  </div>
);

export const Denied = ({ text = "You do not have permission to view this page." }) => (
  <div data-testid="permission-denied" className="mx-auto max-w-md mt-16 rounded-md border border-red-200 bg-white p-8 text-center">
    <ShieldAlert className="mx-auto h-10 w-10 text-red-600" />
    <h2 className="mt-3 text-lg font-semibold">Access denied</h2>
    <p className="mt-1 text-sm text-slate-600">{text}</p>
    <Link to="/" className="mt-4 inline-block text-sm font-medium text-brand underline">Back to dashboard</Link>
  </div>
);

export const ErrorBox = ({ text, onRetry }) => (
  <div data-testid="error-state" className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-center justify-between gap-3">
    <span>{text}</span>
    {onRetry && <button onClick={onRetry} className="font-semibold underline" data-testid="error-retry-btn">Retry</button>}
  </div>
);

export const BrandLogo = ({ branding, size = 40, testid = "brand-logo" }) => {
  const [broken, setBroken] = useState(false);
  const url = assetUrl(branding?.logo_url);
  if (url && !broken) {
    return <img data-testid={testid} src={url} alt={`${branding.lgu_name} logo`} onError={() => setBroken(true)} style={{ width: size, height: size }} className="rounded-full object-contain bg-white" />;
  }
  return (
    <div data-testid={`${testid}-fallback`} aria-label="LGU seal placeholder" style={{ width: size, height: size }} className="rounded-full bg-brand flex items-center justify-center ring-2 ring-white/60">
      <Landmark style={{ width: size * 0.5, height: size * 0.5 }} />
    </div>
  );
};

export const Btn = ({ variant = "brand", className = "", ...p }) => {
  const v = {
    brand: "btn-brand",
    outline: "border border-slate-300 bg-white text-slate-800 hover:bg-slate-50",
    danger: "bg-red-700 text-white hover:bg-red-800",
    warn: "bg-amber-600 text-white hover:bg-amber-700",
    ghost: "text-slate-700 hover:bg-slate-100",
  }[variant];
  return <button {...p} className={`inline-flex items-center justify-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${v} ${className}`} />;
};

export const fmtDate = (s) => (s ? new Date(s).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" }) : "—");
export const fmtVal = (f, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (f?.type === "bool") return v ? "Yes" : "No";
  if (f?.key?.endsWith("_at")) return fmtDate(v);
  if (f?.system && typeof v === "string" && /^[a-z_]+$/.test(v)) return v.replace(/_/g, " ");
  if ((f?.type === "number" || f?.type === "fee") && /amount|budget|cost|fee/i.test(f.key)) return `₱${Number(v).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;
  return String(v);
};
